import React from 'react';
import { motion } from 'framer-motion';
import { Settings, Focus, Languages, Bot, Blocks, Lightbulb, BarChart2, MessageSquare, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const HeroSection = () => {
  const features = [
    { icon: Focus, label: "Computer Vision", angle: 0 },
    { icon: Languages, label: "Language AI", angle: 45 },
    { icon: Bot, label: "Chatbots", angle: 90 },
    { icon: Blocks, label: "Scalable Solutions", angle: 135 },
    { icon: Lightbulb, label: "Innovation", angle: 225 },
    { icon: BarChart2, label: "Analytics", angle: 270 },
    { icon: Settings, label: "Automation", angle: 315 },
  ];

  return (
    <div className="w-full min-h-[85vh] h-auto lg:h-[85vh] flex flex-col lg:flex-row bg-[#0A0A0A] overflow-hidden relative rounded-[25px]">

      {/* Background grid/dots effect (optional for premium feel) */}
      <div className="absolute inset-0 z-0 opacity-20 pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(#FFB81C 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

      {/* Left Side: Text Content */}
      <div className="w-full lg:w-1/2 pt-20 pb-12 lg:py-0 lg:h-full flex flex-col justify-center px-6 md:px-12 lg:pl-20 xl:pl-24 relative z-20">

        {/* Accent Label */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-6 flex items-center"
        >
          <div className="flex flex-col">
            <span className="text-brand-gold text-sm font-bold tracking-[0.2em] uppercase">
              AIGENIUS DEVELOPMENT CELL
            </span>
            <div className="w-12 h-[2px] bg-brand-gold mt-2" />
          </div>
        </motion.div>

        {/* Main Heading */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1] mb-6"
        >
          Intelligent Solutions<br />
          for a <span className="text-brand-gold">Smarter Future</span>
        </motion.h1>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-base md:text-lg text-gray-400 max-w-lg mb-10 leading-relaxed"
        >
          We design and develop AI-powered solutions that solve real-world problems, automate operations and create measurable impact across industries.
        </motion.p>

        {/* Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="flex flex-wrap items-center gap-4"
        >
          <Link to="/solutions" className="bg-brand-gold hover:bg-brand-goldHover text-black font-semibold py-3 px-6 rounded-lg transition-all flex items-center gap-2 group shadow-[0_0_20px_rgba(255,184,28,0.3)]">
            Explore Our Solutions
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>

          <button className="border border-brand-gold/30 hover:border-brand-gold text-brand-gold hover:bg-brand-gold/5 font-semibold py-3 px-6 rounded-lg transition-all flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Talk to Our Expert
          </button>
        </motion.div>
      </div>

      {/* Right Side: Code-based Visualization matching the banner */}
      <div className="w-full lg:w-1/2 h-[60vh] lg:h-full relative overflow-hidden flex items-center justify-center z-10 px-4 md:px-8">

        {/* Subtle background glow for the right section */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-gold/5 rounded-full blur-[100px] pointer-events-none" />

        {/* Diagram Container with responsive scaling */}
        <div className="relative w-full h-full max-w-[500px] max-h-[500px] flex items-center justify-center scale-[0.6] sm:scale-75 md:scale-90 lg:scale-[0.85] xl:scale-95 transition-transform duration-300 origin-center mt-10 lg:mt-0">

          {/* Base Podium (3D effect) */}
          <div className="absolute top-[65%] left-1/2 -translate-x-1/2 flex flex-col items-center justify-center z-10" style={{ perspective: "1000px" }}>
            <div className="absolute flex items-center justify-center" style={{ transform: "rotateX(75deg)", transformStyle: "preserve-3d" }}>
              {/* Outer rings */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                className="absolute w-[380px] h-[380px] rounded-full border-[1.5px] border-brand-gold/20"
              />
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                className="absolute w-[320px] h-[320px] rounded-full border-2 border-brand-gold/40 border-dashed"
              />
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
                className="absolute w-[260px] h-[260px] rounded-full border-[1.5px] border-brand-gold/30 border-dotted"
              />
              {/* Solid base */}
              <div className="absolute w-[210px] h-[210px] rounded-full bg-[#050505] border-[2px] border-brand-gold/80 shadow-[0_0_60px_rgba(255,184,28,0.4)_inset,0_0_40px_rgba(255,184,28,0.4)] z-10" />

              {/* Center glowing light from podium */}
              <div className="absolute w-24 h-24 bg-brand-gold rounded-full blur-[40px] opacity-80 z-20" style={{ transform: "translateZ(10px)" }} />
            </div>
          </div>

          {/* Central AI Orb (Vertical) */}
          <div className="absolute top-[50%] left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center z-30">
            {/* Outer glowing rings */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
              className="absolute w-[210px] h-[210px] rounded-full border-[2px] border-brand-gold/30 shadow-[0_0_20px_rgba(255,184,28,0.2)]"
            />
            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
              className="absolute w-[180px] h-[180px] rounded-full border-[2px] border-brand-gold/60 border-dashed shadow-[0_0_15px_rgba(255,184,28,0.3)]"
            />
            <div className="absolute w-[150px] h-[150px] rounded-full border-[3px] border-brand-gold shadow-[0_0_40px_rgba(255,184,28,0.6),inset_0_0_30px_rgba(255,184,28,0.5)] bg-[#0a0a0a]/90 backdrop-blur-md flex items-center justify-center z-10">
              <span className="text-6xl font-extrabold text-brand-gold drop-shadow-[0_0_25px_rgba(255,184,28,1)]">
                AI
              </span>
            </div>
          </div>

          {/* Radiating nodes (positioned from the absolute center) */}
          <div className="absolute top-[50%] left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center z-20 w-0 h-0">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div key={idx} className="absolute inset-0 flex items-center justify-center">
                  {/* Connecting line Wrapper */}
                  <div
                    className="absolute w-[1px] origin-bottom"
                    style={{
                      height: '180px', // Distance to the nodes
                      transform: `rotate(${feature.angle}deg) translateY(-100%)`, // Position line
                    }}
                  >
                    <motion.div
                      initial={{ scaleY: 0 }}
                      animate={{ scaleY: 1 }}
                      transition={{ duration: 1, delay: 0.5 + idx * 0.1 }}
                      className="w-full h-full bg-gradient-to-t from-transparent via-brand-gold/50 to-brand-gold/80 origin-bottom"
                    >
                      {/* Traveling dot along the line */}
                      <motion.div
                        animate={{ y: [0, -180] }}
                        transition={{ duration: 2.5, repeat: Infinity, delay: idx * 0.3 }}
                        className="w-2 h-2 bg-white rounded-full shadow-[0_0_15px_rgba(255,184,28,1)] absolute -left-[3.5px]"
                      />
                    </motion.div>
                  </div>

                  {/* Icon Node Wrapper */}
                  <div
                    className="absolute flex flex-col items-center justify-center"
                    style={{
                      transform: `rotate(${feature.angle}deg) translateY(-220px) rotate(-${feature.angle}deg)`
                    }}
                  >
                    <motion.div
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.6, delay: 0.8 + idx * 0.1 }}
                      className="flex flex-col items-center justify-center"
                    >
                      {/* Octagon shape for icon */}
                      <div
                        className="w-16 h-16 flex items-center justify-center bg-[#050505] border-[2px] border-brand-gold shadow-[0_0_20px_rgba(255,184,28,0.4),inset_0_0_10px_rgba(255,184,28,0.2)] hover:shadow-[0_0_35px_rgba(255,184,28,0.8),inset_0_0_15px_rgba(255,184,28,0.4)] transition-all cursor-pointer group mb-3 relative z-20"
                        style={{ clipPath: "polygon(25% 0%, 75% 0%, 100% 25%, 100% 75%, 75% 100%, 25% 100%, 0% 75%, 0% 25%)" }}
                      >
                        <Icon className="w-7 h-7 text-brand-gold group-hover:scale-110 transition-transform drop-shadow-[0_0_10px_rgba(255,184,28,0.8)]" />
                      </div>

                      <span className="text-[13px] text-gray-200 font-semibold whitespace-nowrap drop-shadow-md tracking-wide px-3 py-1 bg-[#050505]/80 rounded-md border border-brand-gold/30 shadow-[0_0_10px_rgba(0,0,0,0.5)]">
                        {feature.label}
                      </span>
                    </motion.div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
